/**
 * findInsertReturningTarget
 *
 * Detect whether the cursor sits inside the RETURNING column list of an
 * INSERT statement, and if so resolve the INSERT's target table. Companion
 * to `findInsertValuesTarget` — same tokenizer, same conventions.
 *
 * INSERT-context only (value-slot plan D11): UPDATE/DELETE/MERGE RETURNING
 * variants return `null` so the generic emission keeps serving them.
 * The cursor must be AFTER a depth-0 `RETURNING` and BEFORE any subsequent
 * depth-0 `INTO` (oracle `RETURNING x INTO :b` / PL/pgSQL `RETURNING x INTO
 * v` put binds/variables after INTO, not target columns).
 *
 * Used by the pg/sqlite/oracle resolvers (T19) to emit ONLY the target
 * table's columns + scalar functions at INSERT RETURNING positions. The
 * mssql OUTPUT analog routes through `findOutputDmlTarget` instead (OUTPUT
 * classifies under the values arm — G12).
 */

import { tokenize } from './tokenizer';
import type { DatabaseEngineId } from '@/core/engines';

export type InsertReturningTarget = {
  readonly name: string;
  readonly schema?: string;
};

const INSERT_MODIFIER_KEYWORDS = new Set([
  'OR', 'IGNORE', 'REPLACE', 'ROLLBACK', 'ABORT', 'FAIL',
  'LOW_PRIORITY', 'HIGH_PRIORITY', 'DELAYED',
]);

export function findInsertReturningTarget(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): InsertReturningTarget | null {
  const tokens = tokenize(source, statementStart, statementEnd, engine);
  if (tokens.length === 0) return null;

  // Statement head must be INSERT / REPLACE (modulo dialect modifiers) —
  // D11's INSERT-context-only rule rejects UPDATE/DELETE/MERGE heads here.
  // A leading CTE prolog (`WITH [RECURSIVE] name [(cols)] AS [[NOT]
  // MATERIALIZED] (…) [, …]`) is skipped first — PG/SQLite allow
  // `WITH … INSERT … RETURNING` (C3 sol finding 8). A cursor INSIDE a CTE
  // body still resolves null (the RETURNING walk below never reaches it),
  // so data-modifying-CTE RETURNING keeps its generic serving.
  let i = 0;
  if (identUpper(tokens[0]) === 'WITH') {
    i = skipCteProlog(tokens);
    if (i < 0) return null;
  }
  const head = identUpper(tokens[i]);
  if (head !== 'INSERT' && head !== 'REPLACE') return null;
  i += 1;
  while (i < tokens.length && INSERT_MODIFIER_KEYWORDS.has(identUpper(tokens[i]) ?? '')) i += 1;
  if (identUpper(tokens[i]) !== 'INTO') return null;
  i += 1;

  // Qualified target name: 1-3 dotted parts; the LAST part is the table.
  if (i >= tokens.length || (tokens[i].type !== 'ident' && tokens[i].type !== 'kw')) return null;
  const parts: string[] = [stripIdentQuotes(tokens[i].text)];
  i += 1;
  while (i + 1 < tokens.length && tokens[i].type === 'dot' && parts.length < 3) {
    const next = tokens[i + 1];
    if (next.type !== 'ident' && next.type !== 'kw') return null;
    parts.push(stripIdentQuotes(next.text));
    i += 2;
  }

  // The cursor must sit after a depth-0 RETURNING with no depth-0 INTO in
  // between. Walk the remaining tokens up to the cursor tracking depth.
  let depth = 0;
  let state: 'before' | 'returning' | 'past-into' = 'before';
  for (; i < tokens.length && tokens[i].start < cursorOffset; i += 1) {
    const t = tokens[i];
    if (t.type === 'lparen') { depth += 1; continue; }
    if (t.type === 'rparen') { depth -= 1; continue; }
    if (t.type === 'semicolon') return null;
    if (depth !== 0) continue;
    const u = identUpper(t);
    if (u === 'RETURNING') state = 'returning';
    else if (u === 'INTO' && state === 'returning') state = 'past-into';
  }
  if (state !== 'returning' || depth !== 0) return null;

  const name = parts[parts.length - 1]!;
  const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return schema !== undefined ? { name, schema } : { name };
}

/** Skips a leading `WITH [RECURSIVE] name [(cols)] AS [[NOT] MATERIALIZED]
 *  (body) [, …]` prolog at token level; returns the index of the first token
 *  after the prolog, or -1 when the prolog is malformed/incomplete (the
 *  caller then refuses the slot). */
function skipCteProlog(tokens: readonly { type: string; text: string; start: number }[]): number {
  let i = 1; // past WITH
  if (identUpper(tokens[i]) === 'RECURSIVE') i += 1;
  for (;;) {
    if (identUpper(tokens[i]) === null) return -1; // CTE name
    i += 1;
    if (tokens[i]?.type === 'lparen') {
      i = skipBalanced(tokens, i);
      if (i < 0) return -1;
    }
    if (identUpper(tokens[i]) !== 'AS') return -1;
    i += 1;
    if (identUpper(tokens[i]) === 'NOT') i += 1;
    if (identUpper(tokens[i]) === 'MATERIALIZED') i += 1;
    if (tokens[i]?.type !== 'lparen') return -1;
    i = skipBalanced(tokens, i);
    if (i < 0) return -1;
    if (tokens[i]?.type === 'comma') { i += 1; continue; }
    return i;
  }
}

/** From an opening lparen at `i`, returns the index just past its matching
 *  rparen, or -1 when unbalanced within the statement. */
function skipBalanced(tokens: readonly { type: string }[], i: number): number {
  let depth = 0;
  for (; i < tokens.length; i += 1) {
    if (tokens[i]!.type === 'lparen') depth += 1;
    else if (tokens[i]!.type === 'rparen') {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

function identUpper(t: { type: string; text: string } | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return stripIdentQuotes(t.text).toUpperCase();
}

function stripIdentQuotes(s: string): string {
  if (s.length < 2) return s;
  const first = s[0];
  const last = s[s.length - 1];
  if (first === '`' && last === '`') return s.slice(1, -1);
  if (first === '"' && last === '"') return s.slice(1, -1);
  if (first === '[' && last === ']') return s.slice(1, -1);
  return s;
}
